import React, { useContext } from 'react';
import Styled from 'styled-components';
import format from 'date-fns/format';
import addMinutes from 'date-fns/addMinutes';
import { sv, nb, fi, enGB } from 'date-fns/locale';
import { Trans } from '@lingui/react';
import Hero from '@ecosystems/landing_pages/hero/Hero';
import { Flex } from '@components/layout/Grid';
import InstructorCircle from '@ecosystems/programs/instructorCircle';
import LightningIcon from '@assets/LightingIcon';
import ClockIcon from '@assets/ClockIcon';
import CalendarIcon from '@assets/CalendarIcon';
import InfoOutlineIcon from '@assets/InfoOutlineIcon';
import { getEventSpotsStatus } from './EventListingCard';
import {
  TextWrapper,
  Title,
  VideoWrapper,
  SubTitle,
  Disclaimer,
  TextWrapperInner,
} from '@ecosystems/landing_pages/hero/components';
import LanguageContext from '@lib/contexts/languageContext';
import { LiveEvent } from '@gql/generated';
import { LIVE_KINDS } from '@lib/constants';

const HeroSection = Styled(Hero)`
  align-items: center;
  width: 100%;
  margin-top: 15px;

  ${TextWrapper} {
    padding-right: 8%;
    padding-top: 20px;
    padding-bottom: 20px;
    margin: 0 auto;
    z-index: 0;

    ${TextWrapperInner} {
      max-width: 720px;
    }
    
    ${Title} {
      display: flex;
      align-items: center;
      font-size: 34px;
      line-height: 1.2;
      margin-bottom: 0px;

      span {
        font-size: 11px;
        color: ${({ theme }) => theme.colors.neutrals.white};
        font-weight: 500;
        border-radius: 3px;
        padding: 5px;
        margin-left: 12px;
        background-color: #129EDC;
      }
    }

    ${SubTitle} {
      font-size: 20px;
      line-height: 1.5;
      margin-top: 24px;
      margin-bottom: 24px;
    }

    ${Disclaimer} {
      font-size: 13px;
      margin-top: 10px;
    }

    @media (max-width: ${props => props.theme.viewports.tablet}px) {
      ${Title} {
        font-size: 24px;
      }
      padding: 0;
      margin-top: 20px;
    }

    @media (max-width: ${props => props.theme.viewports.mobile}px) {
      padding-right: 0;
    }
  }

  ${VideoWrapper} {
    position: relative;

    :before {
      content: "";
      display: block;
      width: 100%;
      padding-top: 50%;
    }
  }
`;

const ExpiredBanner = Styled.p`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 50px;
  font-size: 13px;
  font-weight: 500;
  padding: 18px 20px;
  margin: 0;
  background-color: #FAE0EB;
  z-index: 1;

  svg {
    margin-right: 5px;
  }
`;

const ComingSoonBanner = Styled(ExpiredBanner)`
  color: #fff;
  background-color: #1c343e;
  opacity: 0.9;

  svg path {
    fill: white;
  }
`;

const EventImage = Styled.img`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  object-fit: cover;
  border-radius: 4px;
`;

const Tag = Styled.span<{ outline?: boolean; plain?: boolean; full?: boolean }>`
  display: flex;
  align-items: center;
  font-size: ${({ plain }) => (plain ? 14 : 11)}px;
  color: ${({ full }) => (full ? 'white' : '#1c343e')};
  font-weight: 600;
  line-height: 1;
  text-transform: capitalize;
  ${({ plain }) => (plain ? '' : 'border: 1px solid #1c343e;')}
  border-radius: 3px;
  padding: 5px;
  margin-right: 5px;
  background-color: ${({ full }) => (full ? '#1c343e' : 'transparent')};

  svg {
    margin-right: 6px;
  }

  :empty {
    display: none;
  }
`;

const EventTitle = Styled.h1`
  font-size: 24px;
  color: #243240;
  font-weight: bold;
  line-height: 1.3;
  margin-bottom: 0;
`;

const EventInformations = Styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-wrap: wrap;
  border-radius: 4px;
  padding: 18px 15px;
  margin-top: 10px;
  background-color: white;


  @media(min-width: ${({ theme }) => theme.viewports.laptop}px){
    flex-direction: row;
  }
`;

const EventInformation = Styled.p`
  min-width: 50%;
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 500;
  text-transform: capitalize;
  margin: 0;

  :nth-last-of-type(n+2){
    margin: 0  0 10px;
  }

  svg {
    height: 16px;
    margin-right: 8px;
  }

  &:nth-of-type(2) {
    order: -1;
  }


  @media(min-width: ${({ theme }) => theme.viewports.laptop}px){
    &:nth-of-type(2) {
      justify-content: flex-end;
      order: unset;
    }
  }
`;

const localeMap = {
  en: enGB,
  se: sv,
  no: nb,
  fi,
};

type Props = {
  event: LiveEvent;
};

const EventDetails = ({ event }: Props) => {
  const instructor = event.instructors?.[0];
  const lang = useContext(LanguageContext);
  const eventDate = new Date(event.eventDates[0]);
  const fullDate = format(eventDate, 'EEEE dd MMMM yyyy', {
    locale: localeMap[lang],
  });

  const startTime = format(eventDate, 'H:mm');
  const endTime = format(addMinutes(eventDate, event.duration), 'H:mm OOO');

  const expiredEvent = event.isExpired;
  const comingSoonEvent = event.isComingSoon;

  return (
    <HeroSection
      description={
        <Flex flexDirection="column">
          <Flex>
            <Tag full>{LIVE_KINDS[event.kind]}</Tag>
            <Tag outline>{event.duration} MIN</Tag>
            <Tag plain>
              <LightningIcon />
              {event.theme}
            </Tag>
          </Flex>
          <EventTitle>{event.title}</EventTitle>
          {instructor ? (
            <InstructorCircle
              instructor={{
                id: instructor?.slug,
                name: instructor?.name,
                assets: {
                  squareImage: instructor?.assets.squareImage,
                },
              }}
            />
          ) : null}
          <EventInformations>
            <EventInformation>
              <CalendarIcon />
              {fullDate}
            </EventInformation>
            <EventInformation>{getEventSpotsStatus(event)}</EventInformation>
            <EventInformation>
              <ClockIcon />
              {startTime} - {endTime}
            </EventInformation>
          </EventInformations>
        </Flex>
      }
      video={
        <>
          {expiredEvent ? (
            <ExpiredBanner>
              <InfoOutlineIcon />
              <Trans id="live.event_page.booking_closed_banner" />
            </ExpiredBanner>
          ) : null}
          {comingSoonEvent ? (
            <ComingSoonBanner>
              <InfoOutlineIcon />
              <Trans id="live.event_page.coming_soon" />
            </ComingSoonBanner>
          ) : null}
          <EventImage src={event.thumbnail} />
        </>
      }
    />
  );
};

export default EventDetails;
